import React, { useState } from "react";
import { Dialog, Switch, FormControlLabel } from "@mui/material";
import { X } from "@phosphor-icons/react";

interface ExportOptionsModalProps {
  open: boolean;
  onClose: () => void;
  onExport: (includeLinks: boolean) => void;
  exportType: string;
}

const ExportOptionsModal: React.FC<ExportOptionsModalProps> = ({
  open,
  onClose,
  onExport,
  exportType,
}) => {
  const [includeLinks, setIncludeLinks] = useState(true);

  const handleExport = () => {
    onExport(includeLinks);
    onClose();
  };

  const getExportTypeLabel = () => {
    switch (exportType) {
      case "docx":
        return "Word (.docx)";
      case "pdf":
        return "PDF";
      case "clipboard-html":
        return "Copy to Clipboard (HTML)";
      case "clipboard-markdown":
        return "Copy as Markdown";
      default:
        return "Export";
    }
  };

  const getButtonLabel = () => {
    return exportType === "clipboard-html" ||
      exportType === "clipboard-markdown"
      ? "Copy"
      : "Export";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          width: "400px",
          maxWidth: "90vw",
        },
      }}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Export Options
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-0 py-2 mb-4">
          <p className="text-gray-600 text-sm mb-4">
            Exporting as:{" "}
            <span className="font-medium">{getExportTypeLabel()}</span>
          </p>

          <FormControlLabel
            control={
              <Switch
                checked={includeLinks}
                onChange={(e) => setIncludeLinks(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#1f2937", // gray-800
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#1f2937", // gray-800
                  },
                }}
              />
            }
            label="Include Citations"
            className="text-gray-700"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-900 transition-colors duration-200"
          >
            {getButtonLabel()}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ExportOptionsModal;
